import {
  Button,
  Card,
  Form,
  InputNumber,
  Switch,
  Typography,
  Alert,
} from 'antd';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAuth } from '@frontegg/react';
import { useDispatch } from 'react-redux';

import { SuperAdminContext, SuperAdminScope } from 'src/components';
import { usePageHeader } from 'src/hooks/misc';
import { useSuperAdminApiOverrides } from 'src/hooks/superAdmin';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import { api, getApiErrorMessage } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import OrganizationFilter from '../OrganizationFilter';

type DataRetentionFormValues = {
  aura_archival_enabled: boolean;
  aura_archival_days: number;
  vca_archival_enabled: boolean;
  vca_archival_days: number;
};

type DataRetentionResponse = {
  settings?: Partial<DataRetentionFormValues>;
};

const DEFAULT_VALUES: DataRetentionFormValues = {
  aura_archival_enabled: true,
  aura_archival_days: 21,
  vca_archival_enabled: true,
  vca_archival_days: 60,
};

const NOT_FOUND_VALUES: DataRetentionFormValues = {
  aura_archival_enabled: false,
  aura_archival_days: 0,
  vca_archival_enabled: false,
  vca_archival_days: 0,
};

const normalizeSettings = (
  settings?: Partial<DataRetentionFormValues>
): DataRetentionFormValues => {
  const auraDays = Number(settings?.aura_archival_days);
  const vcaDays = Number(settings?.vca_archival_days);

  return {
    aura_archival_enabled:
      typeof settings?.aura_archival_enabled === 'boolean'
        ? settings.aura_archival_enabled
        : DEFAULT_VALUES.aura_archival_enabled,
    aura_archival_days:
      Number.isFinite(auraDays) && auraDays > 0
        ? auraDays
        : DEFAULT_VALUES.aura_archival_days,
    vca_archival_enabled:
      typeof settings?.vca_archival_enabled === 'boolean'
        ? settings.vca_archival_enabled
        : DEFAULT_VALUES.vca_archival_enabled,
    vca_archival_days:
      Number.isFinite(vcaDays) && vcaDays > 0
        ? vcaDays
        : DEFAULT_VALUES.vca_archival_days,
  };
};

const getValuesSnapshot = (values?: Partial<DataRetentionFormValues>): string =>
  JSON.stringify({
    aura_archival_enabled: Boolean(values?.aura_archival_enabled),
    aura_archival_days: Number(values?.aura_archival_days ?? 0),
    vca_archival_enabled: Boolean(values?.vca_archival_enabled),
    vca_archival_days: Number(values?.vca_archival_days ?? 0),
  });

const DataRetentionContent = () => {
  const { user } = useAuth();
  const dispatch = useDispatch<any>();
  const { org } = useContext(SuperAdminContext);
  const orgUuid = org?.org_uuid;
  const hasOrgSelected = Boolean(orgUuid);
  const { headers: superAdminHeaders } = useSuperAdminApiOverrides();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<DataRetentionFormValues>();
  const lastSyncedValuesSnapshotRef = useRef<string | undefined>(undefined);
  const lastSyncedOrgUuidRef = useRef<string | undefined>(undefined);

  usePageHeader({
    title: 'Data Retention',
  });

  useEffect(() => {
    dispatch(getAllActiveOrganizationAction());
  }, [dispatch]);

  const headers = useMemo(
    () => ({
      ...(superAdminHeaders || {}),
    }),
    [superAdminHeaders]
  );

  const {
    data,
    isLoading: isLoadingSettings,
    isFetching: isFetchingSettings,
    isError: isSettingsError,
  } = useQuery({
    queryKey: ['organization-settings', 'archival-settings', orgUuid],
    enabled: hasOrgSelected,
    queryFn: async () => {
      try {
        const response = await api.get<DataRetentionResponse>(
          '/organization_settings',
          {
            headers,
            params: {
              key: 'archival_settings',
            },
          }
        );

        return {
          values: normalizeSettings(response.data?.settings),
          isNotFound: false,
        };
      } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 404) {
          return {
            values: NOT_FOUND_VALUES,
            isNotFound: true,
          };
        }
        throw error;
      }
    },
  });

  useEffect(() => {
    if (!hasOrgSelected) {
      form.setFieldsValue(DEFAULT_VALUES);
      lastSyncedValuesSnapshotRef.current = getValuesSnapshot(DEFAULT_VALUES);
      lastSyncedOrgUuidRef.current = undefined;
      return;
    }

    const nextValues = data?.values || DEFAULT_VALUES;
    const hasOrgChanged = lastSyncedOrgUuidRef.current !== orgUuid;
    const currentValues = form.getFieldsValue(
      true
    ) as Partial<DataRetentionFormValues>;
    const hasUnsavedChanges = lastSyncedValuesSnapshotRef.current
      ? getValuesSnapshot(currentValues) !== lastSyncedValuesSnapshotRef.current
      : form.isFieldsTouched(true);

    if (hasOrgChanged || !hasUnsavedChanges) {
      form.setFieldsValue(nextValues);
      lastSyncedValuesSnapshotRef.current = getValuesSnapshot(nextValues);
      lastSyncedOrgUuidRef.current = orgUuid;
    }
  }, [data?.values, form, hasOrgSelected, orgUuid]);

  const { mutate: saveSettings, isPending: isSaving } = useMutation({
    mutationFn: async (values: DataRetentionFormValues) => {
      const payload: DataRetentionFormValues = {
        aura_archival_enabled: values.aura_archival_enabled,
        aura_archival_days: Number(values.aura_archival_days),
        vca_archival_enabled: values.vca_archival_enabled,
        vca_archival_days: Number(values.vca_archival_days),
      };

      return api.put(
        '/organization_settings',
        {
          settings: payload,
        },
        {
          headers,
          params: {
            key: 'archival_settings',
          },
        }
      );
    },
    onSuccess: (_, submittedValues) => {
      showToastSuccess('Data retention settings updated successfully.');
      lastSyncedValuesSnapshotRef.current = getValuesSnapshot(submittedValues);
      lastSyncedOrgUuidRef.current = orgUuid;
      queryClient.invalidateQueries({
        queryKey: ['organization-settings', 'archival-settings', orgUuid],
      });
    },
    onError: (error: any) => {
      showToastError(
        getApiErrorMessage(error) || 'Failed to update data retention settings.'
      );
    },
  });

  const auraEnabled = Form.useWatch('aura_archival_enabled', form);
  const vcaEnabled = Form.useWatch('vca_archival_enabled', form);
  const isSettingsNotFound = Boolean(data?.isNotFound);
  const hasBlockingLoadError = isSettingsError && !isSettingsNotFound;
  const fronteggRoleKeys = useMemo(
    () =>
      (user?.roles || []).map((role: { key?: string }) =>
        String(role?.key || '').toLowerCase()
      ),
    [user?.roles]
  );
  const hasSuperadminRole = fronteggRoleKeys.some((key) =>
    key.includes('superadmin')
  );
  const canWriteDataRetention = fronteggRoleKeys.includes(
    'data-retention-write'
  );
  const isReadOnly = hasSuperadminRole && !canWriteDataRetention;
  const canSubmitForm =
    hasOrgSelected &&
    !isLoadingSettings &&
    !isFetchingSettings &&
    !hasBlockingLoadError &&
    !isReadOnly;

  return (
    <div className="page-wrapper">
      <OrganizationFilter />
      <div className="py-3 flex justify-end">
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={isSaving}
          disabled={!canSubmitForm}
        >
          Save
        </Button>
      </div>
      {!hasOrgSelected && (
        <Alert
          type="info"
          showIcon
          message="Select an organization to configure data retention settings."
          className="mb-4"
        />
      )}
      {isReadOnly && (
        <Alert
          type="warning"
          showIcon
          message="Read-only access: only users with the 'Data Retention Write' role can modify these settings."
          className="mb-4"
        />
      )}
      {hasBlockingLoadError && (
        <Alert
          type="error"
          showIcon
          message="Unable to load data retention settings."
          className="mb-4"
        />
      )}
      <Card loading={isLoadingSettings || isFetchingSettings} bordered>
        <div className="mb-4">
          <Typography.Title level={5} className="!mb-1">
            Data retention by account
          </Typography.Title>
          <Typography.Text type="secondary">
            Configure archival behavior for Aura and VCA.
          </Typography.Text>
        </div>

        <Form<DataRetentionFormValues>
          form={form}
          layout="vertical"
          initialValues={DEFAULT_VALUES}
          disabled={!canSubmitForm}
          onFinish={(values) => {
            if (!canSubmitForm) return;
            saveSettings(values);
          }}
        >
          <div className="flex items-center justify-between py-4 border-b border-[#f0f0f0] gap-4">
            <div className="flex items-center gap-3">
              <Form.Item
                name="aura_archival_enabled"
                valuePropName="checked"
                className="!mb-0"
              >
                <Switch />
              </Form.Item>
              <Typography.Text strong>Aura</Typography.Text>
            </div>
            {auraEnabled && (
              <div className="flex items-center gap-2">
                <Form.Item
                  name="aura_archival_days"
                  className="!mb-0"
                  help=""
                  rules={[
                    { required: true, message: 'Enter number of days' },
                    {
                      type: 'number',
                      min: 1,
                      message: 'Days should be at least 1',
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    precision={0}
                    placeholder="Number of days"
                    style={{ width: 180 }}
                  />
                </Form.Item>
                <Typography.Text type="secondary">Days</Typography.Text>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between py-4 gap-4">
            <div className="flex items-center gap-3">
              <Form.Item
                name="vca_archival_enabled"
                valuePropName="checked"
                className="!mb-0"
              >
                <Switch />
              </Form.Item>
              <Typography.Text strong>VCA</Typography.Text>
            </div>
            {vcaEnabled && (
              <div className="flex items-center gap-2">
                <Form.Item
                  name="vca_archival_days"
                  className="!mb-0"
                  help=""
                  rules={[
                    { required: true, message: 'Enter number of days' },
                    {
                      type: 'number',
                      min: 1,
                      message: 'Days should be at least 1',
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    precision={0}
                    placeholder="Number of days"
                    style={{ width: 180 }}
                  />
                </Form.Item>
                <Typography.Text type="secondary">Days</Typography.Text>
              </div>
            )}
          </div>
        </Form>
      </Card>
    </div>
  );
};

const DataRetentionPage = () => (
  <SuperAdminScope>
    <DataRetentionContent />
  </SuperAdminScope>
);

export default DataRetentionPage;
